import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';

const text = (value) => (value == null ? '' : String(value).trim());

function formatAge(visit = {}) {
  const days = Number(visit?.ageInDaysAtVisit);
  if (Number.isFinite(days) && days >= 0) {
    if (days < 60) return `${Math.floor(days)} day${Math.floor(days) === 1 ? '' : 's'}`;
    if (days < 365.2425) {
      const months = Math.floor(days / 30.4375);
      const remainingDays = Math.max(0, Math.floor(days - (months * 30.4375)));
      return `${months} month${months === 1 ? '' : 's'}${remainingDays ? ` ${remainingDays} day${remainingDays === 1 ? '' : 's'}` : ''}`;
    }
    const years = Math.floor(days / 365.2425);
    const remainingDays = days - Math.floor(years * 365.2425);
    const months = Math.max(0, Math.floor(remainingDays / 30.4375));
    return `${years} year${years === 1 ? '' : 's'}${months ? ` ${months} month${months === 1 ? '' : 's'}` : ''}`;
  }
  const group = text(visit?.ageGroupAtVisit);
  const legacy = text(visit?.citizenAge);
  return legacy || group;
}

function complaintSummary(visit = {}) {
  if (!Array.isArray(visit?.chiefComplaints) || !visit.chiefComplaints.length) return '';
  return visit.chiefComplaints.slice(0, 4).map((item) => {
    const complaint = text(item?.complaint || item?.label);
    const value = item?.durationValue;
    const unit = text(item?.durationUnit);
    if (complaint && value !== '' && value != null && unit) return `${complaint} ${value} ${unit.toLowerCase()}`;
    return complaint;
  }).filter(Boolean).join(' · ');
}

export default function VisitWorkspaceHeader({ visit }) {
  const age = formatAge(visit || {});
  const sex = text(visit?.citizenGender);
  const pathway = text(visit?.clinicalPathway || visit?.clinicalFocus);
  const problem = text(visit?.primaryPresentingProblem || visit?.complaintGroup);
  const serviceArea = text(visit?.serviceArea || visit?.visitCategory);
  const maternal = text(visit?.maternalContext).toLowerCase();
  const showMaternal = maternal && !['unspecified', 'neither', 'not specified'].includes(maternal) && sex.toLowerCase() !== 'male';
  const chiefComplaintText = complaintSummary(visit || {});
  const additional = Array.isArray(visit?.additionalPathways)
    ? visit.additionalPathways.filter((item) => item?.active !== false)
    : [];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6">{visit?.citizenName || 'Visit Workspace'}</Typography>
          <Typography variant="body2">
            Visit ID: <strong>{visit?.visitId || '—'}</strong> &nbsp;•&nbsp; Citizen ID: <strong>{visit?.citizenId || '—'}</strong>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {age || sex ? <Chip size="small" label={[age, sex].filter(Boolean).join(' • ')} /> : null}
          {visit?.centre ? <Chip size="small" label={visit.centre} /> : null}
          {visit?.status ? <Chip size="small" color="primary" variant="outlined" label={String(visit.status).toUpperCase()} /> : null}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
        {serviceArea ? <Chip size="small" label={`Service Area: ${serviceArea}`} /> : null}
        {pathway ? <Chip size="small" label={`Primary Pathway: ${pathway}`} /> : null}
        {problem ? <Chip size="small" label={`Primary Complaint: ${problem}`} /> : null}
        {showMaternal ? <Chip size="small" label={`Maternal Context: ${visit.maternalContext}`} /> : null}
        {visit?.triagePriority ? <Chip size="small" label={`Triage: ${visit.triagePriority}`} /> : null}
      </Box>

      {chiefComplaintText ? (
        <Typography variant="body2" sx={{ mt: 1.25 }}>
          <strong>Chief complaints:</strong> {chiefComplaintText}
        </Typography>
      ) : null}

      {additional.length ? (
        <Box sx={{ mt: 1.25 }}>
          <Typography variant="caption" color="text.secondary">Additional clinical pathways</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {additional.map((item, index) => (
              <Chip
                size="small"
                variant="outlined"
                key={item.pathwayId || `${item.clinicalPathway || item.serviceArea || 'pathway'}-${index}`}
                label={item.presentingProblem || item.clinicalPathway || item.serviceArea || `Pathway ${index + 1}`}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Paper>
  );
}
